import React, { useEffect, startTransition } from 'react'
import { useEnv } from '@praxis/component-runtime-env'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import {
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import {
  Box,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { formatDate } from '../../helpers/dateHelper'
import Grid from '@material-ui/core/Grid'
import ImageNA from '../../images/ImageNA.svg'
import Divider from '@material-ui/core/Divider'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import Drawer from '@material-ui/core/Drawer'
import { setBreadcrumbs } from '../../store/Layout/actionCreator'
import { useParams } from 'react-router-dom'
import { orderBy } from 'lodash'
import EditProject from './EditProject'
import CustomTable from '../../components/CustomTable'
import CustomTabs from '../../components/CustomTabs'
import {
  customizeColumns,
  tcinHeaders,
  generateDefaultColumns,
  projectTabLabels as projectTabLabelsList,
  metricsTableCols,
  itemStateLabels,
} from './constants'
import TableToolbar from './TableToolbar'
import TabData from './TabData'
import EditIcon from '@material-ui/icons/Edit'
import apiConfig from '../../apiConfig'
import {
  getProjectInfo,
  getProjectPackagingInfo,
  getAdditionalAssetsInfo,
  getCustomColumnsList,
  updateCustomColumnsList,
  archiveProjects,
  resetProjectInfo,
  resetProjectMetricInfo,
} from '../../store/Dashboard/actionCreator'
import { toSafeData } from '../../containers/TcinInfo/mapper'
import { withAuth } from '@praxis/component-auth'
import { producerADGroup } from '../../constants/common'
import CloseIcon from '@material-ui/icons/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { getSubCompanyList } from '../Workflow/constants'
const useStyles = makeStyles((theme) => ({
  root: {
    flexDirection: 'column',
    width: '100%',
  },
  topMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: 16,
  },
  header: {
    display: 'flex',
    padding: '16px 24px',
    alignItems: 'center',
    gap: 16,
  },
  buttons: {
    height: '36px',
    minWidth: '110px',
  },
  editButton: {
    color: '#FFFFFF',
    backgroundColor: '#366CD9',
    '&:hover': {
      backgroundColor: '#1976d2',
    },
  },
  tagsButton: {
    padding: '0 !important',
    color: '#366CD9',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '14px',
    '&:hover': {
      outline: 'none',
      background: 'none !important',
    },
  },
  ownerDetails: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '16px',
    color: '#333333',
  },
  itemName: {
    color: '#666666',
  },
  itemDetails: {
    color: '#333333',
    fontWeight: '500',
  },
  drawerHeader: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '20px',
    lineHeight: '23px',
    flex: '1',
    color: '#333333',
    margin: '0',
  },
  closeIcon: {
    padding: '0 !important',
    marginRight: '-9px !important',
  },
  attrDiv: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  attrHead: {
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: 12,
    lineHeight: '14.06px',
  },
  attrValue: {
    fontFamily: 'Roboto',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: 16,
    lineHeight: '18.75px',
  },
  drawerHeaderDivider: {
    marginTop: '24px',
    width: `calc(100% + 24px)`,
    marginLeft: '0px',
    '&:before': {
      content: "''",
      width: '24px',
      height: '1px',
      display: 'block',
      position: 'absolute',
      background: 'rgba(0, 0, 0, 0.12)',
      marginLeft: '-24px',
    },
  },
  drawerContainer: {
    padding: '40px 0',
  },
  imgInfo: {
    width: 'fit-content',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#EDEDED',
    color: theme.palette.text.secondary,
    '& svg': {
      margin: theme.spacing(1.5),
    },
    '& hr': {
      margin: theme.spacing(0, 0.5),
    },
  },
  drawer: {
    width: '80%',
    maxWidth: '1200px',
    padding: 24,
  },
  tabDrawer: {
    minWidth: 1100,
    width: '75%',
    overflowY: 'unset',
  },
  metricsGrid: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
    padding: '4px 0',
  },
  metricCard: {
    minWidth: 140,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  metricGauge: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 6,
  },
  metricRing: {
    position: 'relative',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricRingInner: {
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
  },
  metricGaugeTrack: {
    color: 'rgba(0, 0, 0, 0.12)',
  },
  metricGaugeValue: {
    position: 'absolute',
    left: 0,
  },
  metricValue: {
    position: 'absolute',
    fontSize: 16,
    fontWeight: 600,
    color: '#333333',
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#666666',
    textTransform: 'uppercase',
    textAlign: 'center',
  },
}))

const ProjectDetail = React.memo((props) => {
  let { pageId: urlProjectId = '' } = useParams()
  const env = useEnv()
  const {
    enablePackaging = false,
    enableAdditionalImages = false,
    restrictedUsers = [],
  } = env
  const dispatch = useDispatch()
  const { pathname: curPagePathName = '' } = props
  const memberOfGroups = Array.isArray(props.memberOf) ? props.memberOf : []
  const isRestrictedUser = restrictedUsers.some((group) =>
    memberOfGroups.includes(group)
  )
  const projectTabLabels = React.useMemo(() => {
    const projectTabs = new Set()
    if (!enableAdditionalImages) projectTabs.add('additionalImages')
    if (!enablePackaging) projectTabs.add('packaging')
    if (isRestrictedUser) projectTabs.add('metrics')

    return projectTabLabelsList?.filter((tab) => !projectTabs.has(tab?.value))
  }, [enableAdditionalImages, enablePackaging, isRestrictedUser])
  const projectDataReducer = useSelector(
    (state) => state.dashboardReducer?.projectDataReducer || {}
  )
  const projectMetricReducer = useSelector(
    (state) => state.dashboardReducer?.projectMetricReducer || {}
  )
  const photobridgeDataMap = useSelector(
    (state) => state.dashboardReducer?.photobridgeData || {}
  )
  const {
    tcinInfo,
    projectName,
    projectId,
    projectLoading,
    repurposeLoading,
    isArchived,
    loginId,
    projectUsers,
    customColumnsList,
  } = useSelector(
    ({
      dashboardReducer: {
        projectDataReducer: {
          tcin_info = [],
          project_name = '',
          project_id = '',
          archived: isArchived = true,
          created_by: { login_id = '' } = {},
          users: projectUsers = [],
        } = {},
        projectLoading = false,
        repurposeLoading = false,
        customColumnsList = [],
      },
    }) => {
      return {
        tcinInfo: tcin_info,
        projectName: project_name,
        projectId: project_id,
        projectLoading,
        repurposeLoading,
        isArchived,
        loginId: login_id,
        projectUsers,
        customColumnsList,
      }
    },
    shallowEqual
  )

  const classes = useStyles()
  const [projectData, setProjectData] = React.useState({})
  const [columns, setColumns] = React.useState([])
  const [tabData, setTabData] = React.useState(false)
  const [tabIndex, setTabIndex] = React.useState({
    category: 'item',
    rowIndex: 0,
  })
  const [tabContent, setTabContent] = React.useState([])
  const [userCustomizedColumns, setUserCustomizedColumns] = React.useState()
  const [viewType, setViewType] = React.useState('Userbased')
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(50)
  const [expandedPipelineRows, setExpandedPipelineRows] = React.useState({})
  const [openDrawer, setOpenDrawer] = React.useState('')
  const [totalCount, setTotalCount] = React.useState(0)
  const [isEditDisabled, setIsEditDisabled] = React.useState(true)
  const [currentTabValue, setCurrentTabValue] = React.useState(() => {
    return localStorage.getItem('projecttab') || 'default'
  })
  const isPackagingLikeTab =
    currentTabValue === 'packaging' || currentTabValue === 'additionalImages'
  useEffect(() => {
    if (localStorage.getItem('projecttab')) {
      localStorage.removeItem('projecttab')
    }
  }, [])
  const [packagingAssetUploadedDate, setPackagingAssetUploadedDate] =
    React.useState(Date.now())

  const {
    accessToken = '',
    userId = '',
    memberOf: userADGroups = [],
    company = '',
  } = props
  const pbSubCompanyList = getSubCompanyList(company)

  /**
   * Call the getProjectInfo API to get the project details
   * Call the getUserCustomColumns API to get the user's customized columns
   */
  useEffect(() => {
    getProjectInfoCall()
    if (userId) {
      dispatch(getCustomColumnsList({ userId }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curPagePathName, currentTabValue])

  /**
   * Set the project data in the state
   * Set the columns for the table
   * Set the page number
   * Set the total count of the tcins
   * Set the isEditDisabled flag based on the user permissions
   * Set the project tabs content
   * Set the breadcrumbs
   */
  useEffect(() => {
    if (projectDataReducer && Object.entries(projectDataReducer).length > 0) {
      const pathname = '/dashboard/' + urlProjectId
      const customPath = '/Projects/:project_name'
      const columnList = tcinHeaders(
        toSafeData(projectDataReducer, 'max_alt_count', 0)
      )
      const effectiveTabValue =
        currentTabValue === 'additionalImages' ? 'packaging' : currentTabValue
      const curTabColList = columnList?.filter(
        (obj) => obj.tabs.indexOf(effectiveTabValue) !== -1
      )
      const finalTabColList =
        currentTabValue === 'additionalImages'
          ? curTabColList?.map((column) => {
              if (column.id === 'packaging_img') {
                return {
                  ...column,
                  headerName: 'Reusable Assets',
                }
              }
              if (column.id === 'packaging_assets_count') {
                return {
                  ...column,
                  headerName: 'Reusable Assets Count',
                }
              }
              return column
            })
          : curTabColList
      const pagePathName = customPath?.replace(
        ':project_name',
        projectName ?? ''
      )
      dispatch(setBreadcrumbs(pagePathName, pathname))
      setProjectData(projectDataReducer)
      setColumns(finalTabColList)
      setPage(toSafeData(projectDataReducer, 'tcin_pagination_details.page', 0))
      setTotalCount(
        toSafeData(projectDataReducer, 'tcin_pagination_details.total_count', 0)
      )
      setIsEditDisabled(
        !(
          !isArchived &&
          userADGroups.includes(producerADGroup.toUpperCase()) &&
          (userId?.toLowerCase() === loginId?.toLowerCase() ||
            projectUsers
              ?.map((user) => user?.login_id?.toLowerCase())
              .indexOf(userId?.toLowerCase()) > -1)
        )
      )
      setTabContent(projectDataReducer?.tcin_info || [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDataReducer])

  useEffect(() => {
    if (customColumnsList?.length > 0 && !isPackagingLikeTab) {
      applyUserView(customColumnsList || [])
    } else {
      handleNoUserView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customColumnsList])

  useEffect(() => {
    return () => {
      dispatch(resetProjectInfo())
      dispatch(resetProjectMetricInfo())
    }
  }, [dispatch])

  const getProjectInfoCall = (
    page = 0,
    numberOfRows = rowsPerPage,
    searchText = [],
    searchBy = ''
  ) => {
    startTransition(async () => {
      setRowsPerPage(numberOfRows)
      dispatch(resetProjectInfo())
      if (isPackagingLikeTab) {
        callGetProjectPackagingInfo(page, numberOfRows, searchText, searchBy)
      } else {
        await callGetProjectInfo(page, numberOfRows, searchText, searchBy)
        if (currentTabValue === 'metrics' && page === 0) {
          await callGetProjectInfo(
            page,
            3000,
            searchText,
            searchBy,
            currentTabValue !== 'metrics'
          )
        }
      }
    })
  }

  /**
   * Call the getProjectInfo API to get the project details
   */
  const callGetProjectInfo = (
    page = 0,
    numberOfRows = rowsPerPage,
    searchText = [],
    searchBy = '',
    callworkflow = true
  ) => {
    dispatch(
      getProjectInfo({
        page,
        numberOfRows: numberOfRows,
        projectId: urlProjectId,
        accessToken,
        lanId: userId,
        searchText: searchText,
        searchBy: searchBy,
        callWorkflow: callworkflow,
        company: apiConfig.isPol
          ? pbSubCompanyList?.join(',') || company || ''
          : '',
      })
    )
  }

  /**
   * Call the getProjectPackagingInfo API to get the project packaging details
   */
  const callGetProjectPackagingInfo = (
    page = 0,
    numberOfRows = rowsPerPage,
    searchText = [],
    searchBy = ''
  ) => {
    if (currentTabValue === 'additionalImages') {
      dispatch(
        getAdditionalAssetsInfo({
          page,
          numberOfRows: numberOfRows,
          projectId: urlProjectId,
          searchText: searchText,
          searchBy: searchBy,
          currentProjectData: projectDataReducer,
        })
      )
    } else {
      dispatch(
        getProjectPackagingInfo({
          page,
          numberOfRows,
          projectId: urlProjectId,
          searchText,
          searchBy,
          currentProjectData: projectDataReducer,
        })
      )
    }
  }

  const applyUserView = (userColumnList = []) => {
    if (isPackagingLikeTab) {
      userColumnList = [
        ...userColumnList,
        'packaging_img',
        'packaging_assets_count',
      ]
    }
    let userCustomizedColumns = { ...(customizeColumns || {}) }
    // Set the selected property to false for each column
    Object.keys(userCustomizedColumns).forEach((column) => {
      userCustomizedColumns[column].selected = false
    })
    // Set the selected property to true for user selected columns
    userColumnList?.forEach((userColumn) => {
      if (userCustomizedColumns[userColumn]) {
        userCustomizedColumns[userColumn].selected = true
      } else {
        userCustomizedColumns[userColumn] = { selected: true }
      }
    })
    setUserCustomizedColumns({ ...userCustomizedColumns })
  }

  const handleNoUserView = () => {
    setUserCustomizedColumns(generateDefaultColumns({ currentTabValue }))
  }

  const updateUserView = (newUserView) => {
    const columnList = Array.from(getNewColumns(newUserView))
    dispatch(updateCustomColumnsList({ userId, columnList }))
  }

  const getNewColumns = (userView) => {
    const columnList = new Set()
    for (const [key, value] of Object.entries(userView)) {
      value.selected && columnList.add(key)
    }
    return columnList
  }

  const handlePackagingAssetUploadedDateChange = (date) => {
    setPackagingAssetUploadedDate(date)
  }

  const titleCase = (value) => {
    const raw = String(value ?? '')
    const normalized = raw.toUpperCase().trim()
    if (itemStateLabels[normalized]) {
      return itemStateLabels[normalized]
    }
    return raw.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const togglePipelineRow = React.useCallback((tcin) => {
    setExpandedPipelineRows((prev) => ({
      ...prev,
      [tcin]: !prev[tcin],
    }))
  }, [])

  const getSystemStatusFromRow = React.useCallback((row, systemKey) => {
    const statusFromResponse = row?.statusBySystem?.[systemKey]
    if (statusFromResponse) {
      return {
        system_status: statusFromResponse.system_status || 'NOT_STARTED',
        total: statusFromResponse.total || 0,
        completed: statusFromResponse.completed || 0,
        errored: statusFromResponse.errored || 0,
      }
    }

    if (systemKey === 'PEGASUS') {
      return {
        system_status: row.pegasusDone
          ? 'COMPLETED'
          : row.pegasusSampleCount > 0
            ? 'IN_PROGRESS'
            : 'NOT_STARTED',
        total: row.pegasusSampleCount || 0,
        completed: row.pegasusDone ? row.pegasusSampleCount || 0 : 0,
        errored: 0,
      }
    }

    if (systemKey === 'PHOTOBRIDGE') {
      return {
        system_status: row.photoBridgeDone
          ? 'COMPLETED'
          : row.photoBridgeImageCount > 0
            ? 'IN_PROGRESS'
            : 'NOT_STARTED',
        total: row.photoBridgeImageCount || 0,
        completed: row.photoBridgeDone ? row.photoBridgeImageCount || 0 : 0,
        errored: 0,
      }
    }

    if (systemKey === 'PIPELINE') {
      const accepted = row.pipelineAccepted || 0
      const rejected = row.pipelineRejected || 0
      const total = row.pipelineImageCount || accepted + rejected || 0
      return {
        system_status: row.pipelineDone
          ? 'COMPLETED'
          : row.pipelinePartiallyDone
            ? 'IN_PROGRESS'
            : 'NOT_STARTED',
        total,
        completed: accepted,
        errored: rejected,
      }
    }

    if (systemKey === 'ASSETHUB') {
      return {
        system_status: row.assetHubDone
          ? 'COMPLETED'
          : row.assetHubPartiallyDone
            ? 'IN_PROGRESS'
            : 'NOT_STARTED',
        total: row.assetHubImageCount || 0,
        completed: row.assetHubDone ? row.assetHubImageCount || 0 : 0,
        errored: 0,
      }
    }

    return {
      system_status: row.gargantuaDone
        ? 'COMPLETED'
        : row.gargantuaPartiallyDone
          ? 'IN_PROGRESS'
          : 'NOT_STARTED',
      total: row.gargantuaImageCount || 0,
      completed: row.gargantuaAccepted || 0,
      errored: row.gargantuaRejected || 0,
    }
  }, [])

  const renderSystemStatusCell = React.useCallback(
    (row, systemKey, isExpanded = false) => {
      const statusInfo = getSystemStatusFromRow(row, systemKey)
      const systemStatus = String(statusInfo.system_status || 'NOT_STARTED')
      const normalizedStatus = systemStatus.toUpperCase()
      const total = statusInfo.total || 0
      const completed = statusInfo.completed || 0
      const errored = statusInfo.errored || 0

      const statusTooltipText =
        (systemKey === 'PIPELINE' || systemKey === 'GARGANTUA'
          ? systemKey === 'PIPELINE'
            ? row.pipelineTooltipText
            : row.gargantuaTooltipText
          : null) ||
        (total
          ? `${completed}/${total} accepted · ${errored} rejected`
          : 'No images processed')

      let inProgressTooltip = 'Not yet started'
      switch (systemKey) {
        case 'PEGASUS':
          inProgressTooltip = row.sampleStatus
          break
        case 'PHOTOBRIDGE':
          inProgressTooltip = `${row.imagesDone} images`
          break
        case 'GARGANTUA':
          inProgressTooltip = statusTooltipText
          break
        case 'PIPELINE':
          inProgressTooltip = statusTooltipText
          break
        case 'ASSETHUB':
          inProgressTooltip = `${row.assetHubCompleted}/${row.assetHubImageCount} published`
          break
        default:
          break
      }

      if (normalizedStatus === 'COMPLETED') {
        const doneChip = (
          <Chip
            size="small"
            label="✓"
            color="success"
            variant="filled"
            style={{
              fontSize: 11,
              height: 20,
              minWidth: 28,
              backgroundColor: '#2e7d32',
              color: '#fff',
            }}
          />
        )
        return systemKey === 'PIPELINE' || systemKey === 'GARGANTUA' ? (
          <Tooltip title={statusTooltipText}>{doneChip}</Tooltip>
        ) : (
          doneChip
        )
      }

      if (
        (systemKey === 'PIPELINE' || systemKey === 'GARGANTUA') &&
        errored > 0
      ) {
        const isPipeline = systemKey === 'PIPELINE'
        return (
          <Tooltip title={statusTooltipText}>
            <Chip
              size="small"
              icon={
                <ErrorOutlineIcon style={{ fontSize: 14, color: '#fff' }} />
              }
              label=""
              color="error"
              variant="filled"
              onClick={
                isPipeline ? () => togglePipelineRow(row.tcin) : undefined
              }
              deleteIcon={
                isPipeline ? (
                  isExpanded ? (
                    <KeyboardArrowUpIcon
                      style={{ fontSize: 14, color: '#fff' }}
                    />
                  ) : (
                    <KeyboardArrowDownIcon
                      style={{ fontSize: 14, color: '#fff' }}
                    />
                  )
                ) : undefined
              }
              onDelete={
                isPipeline ? () => togglePipelineRow(row.tcin) : undefined
              }
              style={{
                fontSize: 11,
                height: 20,
                minWidth: 28,
                cursor: isPipeline ? 'pointer' : 'default',
                backgroundColor: '#cc0000',
                color: '#fff',
              }}
            />
          </Tooltip>
        )
      }

      if (normalizedStatus === 'IN_PROGRESS') {
        return (
          <Tooltip title={inProgressTooltip}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 20,
                borderRadius: '10px',
                backgroundColor: '#ed6c02',
                color: '#fff',
              }}
            >
              <HourglassEmptyIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        )
      }

      return (
        <Tooltip title="Not yet started">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 20,
              borderRadius: '10px',
              backgroundColor: 'rgba(0,0,0,0.08)',
              color: 'rgba(0,0,0,0.4)',
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )
    },
    [getSystemStatusFromRow, togglePipelineRow]
  )

  const renderDrawer = (drawer) => {
    switch (drawer) {
      case 'Edit Project':
        return (
          <>
            <Divider className={classes.drawerHeaderDivider} />
            <EditProject
              projectData={projectData}
              id={urlProjectId}
              closeDialog={handleEditClose}
              isEditDisabled={isEditDisabled}
            />
          </>
        )
      case 'Project Info':
        return (
          <>
            <Divider className={classes.drawerHeaderDivider} />
            <Grid container className={classes.drawerContainer}>
              <Grid
                wrap={'nowrap'}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Typography variant="body1" className={classes.ownerDetails}>
                  {`${projectData.project_name} created by ${
                    projectData.created_by && projectData.created_by.first_name
                  } ${
                    projectData.created_by && projectData.created_by.last_name
                  }  on ${formatDate(projectData.created_date)}`}
                </Typography>
                <div>
                  {userADGroups?.includes(producerADGroup.toUpperCase()) &
                  !apiConfig.isPol ? (
                    <React.Fragment>
                      {userId?.toLowerCase() ===
                      projectData.created_by?.login_id?.toLowerCase() ? (
                        <Button
                          className={classes.buttons}
                          variant="outlined"
                          onClick={() =>
                            makeProjectsArchive(!projectData.archived)
                          }
                          startIcon={<DeleteOutlineIcon />}
                          style={{ marginRight: '24px' }}
                          disabled={
                            userId?.toLowerCase() !==
                            projectData.created_by?.login_id?.toLowerCase()
                          }
                        >
                          {projectData.archived ? 'Undo Archive' : 'Archive'}
                        </Button>
                      ) : (
                        <></>
                      )}

                      {!isEditDisabled && (
                        <Button
                          variant="contained"
                          className={`${classes.buttons} ${classes.editButton}`}
                          onClick={() => setOpenDrawer('Edit Project')}
                          disabled={isEditDisabled}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                      )}
                    </React.Fragment>
                  ) : (
                    <></>
                  )}
                </div>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Project Name:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.project_name}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    PID:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.sap_pid || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Project Type:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.project_type || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Estimated #shots per TCIN:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.shot_count || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Estimated Completion Date:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.due_date
                      ? formatDate(projectData?.due_date)
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Asset Due Date:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemDetails}`}
                  >
                    {projectData?.asset_due_date
                      ? formatDate(projectData?.asset_due_date)
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Owners:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <div className={classes.ownerDetails}>
                    {projectData?.users
                      ? projectData?.users.map((option) => {
                          return (
                            <Chip
                              style={{ marginRight: '8px' }}
                              label={option.display_name}
                            />
                          )
                        })
                      : 'N/A'}
                  </div>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Followers:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <div className={classes.ownerDetails}>
                    {projectData?.viewers
                      ? projectData?.viewers.map((option) => {
                          return (
                            <Chip
                              style={{ marginRight: '8px' }}
                              label={option.display_name}
                            />
                          )
                        })
                      : 'N/A'}
                  </div>
                </Grid>
              </Grid>
              <Grid
                wrap={'nowrap'}
                style={{ marginTop: '40px' }}
                container
                justify={'space-between'}
                alignItems={'center'}
              >
                <Grid item lg={4}>
                  <Typography
                    variant="body1"
                    className={`${classes.ownerDetails} ${classes.itemName}`}
                  >
                    Studio:
                  </Typography>
                </Grid>
                <Grid item lg={8}>
                  <div className={classes.ownerDetails}>
                    {projectData?.studio_name || 'N/A'}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </>
        )

      default:
        break
    }
  }

  const makeProjectsArchive = (makeArchive) => {
    const params = {
      project_ids: [urlProjectId],
      archive: makeArchive,
    }
    dispatch(archiveProjects({ params, callBack: getProjectInfoCall })) //TODO: Implement this & test
  }

  const handleEditClose = (isSaved) => {
    setOpenDrawer('')
  }

  const handleCloseTabData = () => {
    setTabData(false)
  }
  const handleOpenTabData = (e, index, category) => {
    setTabData(true)
    setTabIndex((state) => ({ ...state, category: category, rowIndex: index }))
  }

  const onOrderChange = (data, orderByName, order) => {
    setTabContent(orderBy(data, orderByName, order))
  }

  const renderProjectHealth = () => {
    const totalTcins = Number(projectMetricReducer.total_tcins) || 0
    const toPercent = (value = 0) => {
      if (!totalTcins) return 0
      return Math.round(((Number(value) || 0) / totalTcins) * 100)
    }

    const pegasusPct = toPercent(projectMetricReducer.pegasus_completion)
    const photoBridgePct = toPercent(
      projectMetricReducer.photobridge_completion
    )
    const pipelinePct = toPercent(projectMetricReducer.pipeline_completion)
    const gargantuaPct = toPercent(projectMetricReducer.gargantua_completion)
    const assetHubPct = toPercent(projectMetricReducer.assethub_completion)
    const completionPct = toPercent(projectMetricReducer.total_completion)

    const renderMetric = (label, percent, options = {}) => {
      const safePercent = Math.max(0, Math.min(100, percent))
      const size = options.size ?? 84
      const thickness = options.thickness ?? 6
      const color = options.color ?? '#1976d2'
      const showBlend = options.showBlend ?? false
      const blendColors = options.blendColors ?? []
      const degree = Math.round(safePercent * 3.6)
      const ringStyle = showBlend
        ? {
            width: size,
            height: size,
            background: `conic-gradient(${blendColors.join(',')})`,
            WebkitMaskImage: `conic-gradient(#000 0deg, #000 ${degree}deg, transparent ${degree}deg)`,
            maskImage: `conic-gradient(#000 0deg, #000 ${degree}deg, transparent ${degree}deg)`,
          }
        : null
      const innerSize = size - thickness * 2
      const showEmpty = safePercent === 0
      return (
        <div key={label} className={classes.metricCard}>
          <div className={classes.metricGauge}>
            {showBlend ? (
              showEmpty ? (
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={size}
                  thickness={thickness}
                  className={classes.metricGaugeTrack}
                />
              ) : (
                <div className={classes.metricRing} style={ringStyle}>
                  <div
                    className={classes.metricRingInner}
                    style={{ width: innerSize, height: innerSize }}
                  />
                </div>
              )
            ) : (
              <>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={size}
                  thickness={thickness}
                  className={classes.metricGaugeTrack}
                />
                <CircularProgress
                  variant="determinate"
                  value={safePercent}
                  size={size}
                  thickness={thickness}
                  className={classes.metricGaugeValue}
                  style={{ color }}
                />
              </>
            )}
            <Typography className={classes.metricValue}>
              {safePercent}%
            </Typography>
          </div>
          <Typography className={classes.metricLabel}>{label}</Typography>
        </div>
      )
    }

    return (
      <div className={classes.metricsGrid}>
        {renderMetric('Overall Completion', completionPct, {
          size: 92,
          thickness: 7,
        })}
        {renderMetric('Pegasus', pegasusPct, {
          size: 80,
          thickness: 6,
          color: '#1e88e5',
        })}
        {renderMetric('Gargantua', gargantuaPct, {
          size: 80,
          thickness: 6,
          color: '#1e88e5',
        })}
        {renderMetric('PhotoBridge', photoBridgePct, {
          size: 80,
          thickness: 6,
          color: '#43a047',
        })}
        {renderMetric('Pipeline', pipelinePct, {
          size: 80,
          thickness: 6,
          color: '#fb8c00',
        })}
        {renderMetric('AssetHub', assetHubPct, {
          size: 80,
          thickness: 6,
          color: '#8e24aa',
        })}
      </div>
    )
  }
  const renderMetrics = (tcinProgressRows) => {
    if (projectLoading) {
      return (
        <Box
          sx={{
            minHeight: 360,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )
    }
    return (
      <Box>
        <div className={classes.topMenu}>
          <TableToolbar
            isRemoveDisabled={isEditDisabled}
            setTabContent={setTabContent}
            getProjectInfo={getProjectInfoCall}
            isEnableCustomColumns={!isPackagingLikeTab}
            tabPage="Projects"
            subTabPage={currentTabValue}
            packagingAssetUploadedDate={packagingAssetUploadedDate}
            handlePackagingAssetUploadedDateChange={
              handlePackagingAssetUploadedDateChange
            }
            settingsIcon={false}
            showWorkflowButton={false}
            showDataButton={false}
            showSheetButton={false}
            selectionText={false}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            rows={tcinProgressRows}
          />
        </div>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table
            size="small"
            stickyHeader
            style={{
              '& .MuiTableCell-root': {
                color: '#333333',
                borderColor: 'rgba(0,0,0,0.08)',
              },
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#ffffff',
                color: '#333333',
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: 'rgba(0,0,0,0.02)',
              },
            }}
          >
            <TableHead
              sx={{
                '& .MuiTableCell-root': {
                  fontWeight: 500,
                  paddingY: '12px',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                },
              }}
            >
              <TableRow>
                {metricsTableCols?.map((label) => (
                  <TableCell
                    key={label}
                    style={{
                      backgroundColor: '#fafafa',
                      ...(label === 'Description'
                        ? { width: 200, maxWidth: 200 }
                        : null),
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tcinProgressRows.map((row) => {
                const pipelineStatus = getSystemStatusFromRow(row, 'PIPELINE')
                const hasRejected = (pipelineStatus?.errored || 0) > 0
                const isExpanded = expandedPipelineRows[row.tcin] || false
                return (
                  <React.Fragment key={row.tcin}>
                    <TableRow hover>
                      <TableCell sx={{ fontWeight: 700 }}>{row.tcin}</TableCell>
                      <TableCell>{row.dpci}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                        }}
                      >
                        {row.description}
                      </TableCell>
                      <TableCell>
                        {!row.previewUrl || row.previewUrl?.includes('null') ? (
                          <ImageNA
                            alt="No Image"
                            title="No Image"
                            className={classes.noImage}
                            key={row.tcin}
                            width={75}
                            height={75}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={row.previewUrl}
                            alt={row.tcin}
                            sx={{
                              width: 75,
                              height: 75,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid rgba(255,255,255,0.15)',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{row.brand}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={titleCase(row.itemState)}
                          variant="outlined"
                          style={{
                            height: 20,
                            borderColor: 'rgba(0,0,0,0.15)',
                            color: 'black',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={titleCase(row.sampleStatus)}
                          variant="outlined"
                          style={{
                            height: 20,
                            borderColor: 'rgba(0,0,0,0.15)',
                            color: 'black',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${row.imagesDone} Done`}
                          color={row.imagesDoneComplete ? 'success' : 'warning'}
                          variant="filled"
                          style={{
                            height: 20,
                            backgroundColor:
                              row.finalShotCount === 0 &&
                              row.photoBridgeImageCount === 0 &&
                              !row.pipelineAccepted
                                ? 'none'
                                : row.imagesDoneComplete
                                  ? '#2e7d32'
                                  : '#ed6c02',
                            color:
                              row.finalShotCount === 0 &&
                              row.photoBridgeImageCount === 0 &&
                              !row.pipelineAccepted
                                ? 'black'
                                : '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {renderSystemStatusCell(row, 'PEGASUS')}
                      </TableCell>
                      <TableCell>
                        {renderSystemStatusCell(row, 'GARGANTUA')}
                      </TableCell>
                      <TableCell>
                        {renderSystemStatusCell(row, 'PHOTOBRIDGE')}
                      </TableCell>
                      <TableCell>
                        {renderSystemStatusCell(row, 'PIPELINE', isExpanded)}
                      </TableCell>
                      <TableCell>
                        {renderSystemStatusCell(row, 'ASSETHUB')}
                      </TableCell>
                      <TableCell>
                        {row.allComplete ? (
                          <Chip
                            size="small"
                            label="✓"
                            color="success"
                            variant="filled"
                            style={{
                              fontSize: 11,
                              height: 20,
                              minWidth: 28,
                              backgroundColor: '#2e7d32',
                              color: '#fff',
                            }}
                          />
                        ) : hasRejected ? (
                          <Chip
                            size="small"
                            label="✗"
                            color="error"
                            variant="filled"
                            style={{
                              fontSize: 11,
                              height: 20,
                              minWidth: 28,
                              backgroundColor: '#cc0000',
                              color: '#fff',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 28,
                              height: 20,
                              borderRadius: '10px',
                              backgroundColor: '#ed6c02',
                              color: '#fff',
                            }}
                          >
                            <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Expandable rejected images detail row */}
                    {hasRejected && (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          sx={{
                            py: 0,
                            borderBottom: isExpanded ? undefined : 'none',
                          }}
                        >
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ py: 1.5, px: 2 }}>
                              <Stack spacing={0.5}>
                                {row.rejectedImages.map((rImg, idx) => (
                                  <Stack
                                    key={idx}
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                  >
                                    <ErrorOutlineIcon
                                      style={{ fontSize: 14, color: '#d32f2f' }}
                                    />
                                    <Typography
                                      variant="caption"
                                      style={{
                                        fontWeight: 600,
                                        color: 'black',
                                      }}
                                    >
                                      {rImg.name}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={
                                        rImg.type === 'main' ? 'Main' : 'Alt'
                                      }
                                      variant="outlined"
                                      style={{
                                        fontSize: 10,
                                        height: 18,
                                        borderColor: 'rgba(0,0,0,0.15)',
                                        color: 'black',
                                      }}
                                    />
                                    {rImg.label && (
                                      <Tooltip
                                        title={rImg.rawMessage || rImg.label}
                                        arrow
                                        placement="top"
                                      >
                                        <Chip
                                          size="small"
                                          label={rImg.label}
                                          color="error"
                                          variant="filled"
                                          style={{
                                            fontSize: 10,
                                            height: 18,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            backgroundColor: '#cc0000',
                                            color: '#fff',
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }
  const renderProjectMetricsTable = () => {
    const currentProjectTcinInfo = projectData.tcin_info || []
    const pbWorkflowImages =
      photobridgeDataMap[projectId || urlProjectId]?.tcin_info || []

    const tcinPreviewUrlMap = new Map(
      currentProjectTcinInfo.map((item) => {
        const itemImage = item?.image_information
        const previewUrl =
          itemImage?.main?.source_image?.preview_image_url ||
          item?.item_info?.images?.main ||
          null
        return [item?.tcin, previewUrl]
      })
    )

    const tcinProgressRows = pbWorkflowImages.map((t) => {
      const statusBySystem = t.status_by_system || {}
      const pegasusStatus = statusBySystem.PEGASUS || {}
      const photoBridgeStatus = statusBySystem.PHOTOBRIDGE || {}
      const pipelineStatus = statusBySystem.PIPELINE || {}
      const assetHubStatus = statusBySystem.ASSETHUB || {}
      const gargantuaStatus = statusBySystem.GARGANTUA || {}

      const pegasusTotal = pegasusStatus.total || 0
      const pegasusCompleted = pegasusStatus.completed || 0
      const photoBridgeTotal = photoBridgeStatus.total || 0
      const photoBridgeCompleted = photoBridgeStatus.completed || 0
      const pipelineTotal = pipelineStatus.total || 0
      const pipelineCompleted = pipelineStatus.completed || 0
      const pipelineErrored = pipelineStatus.errored || 0
      const assetHubTotal = assetHubStatus.total || 0

      const finalData = {
        tcin: t.tcin,
        dpci: t.dpci,
        description: t.description || '—',
        brand: t.brand || '—',
        itemState: t.item_status || '—',
        statusBySystem,
        pegasusDone: pegasusStatus.system_status === 'COMPLETED',
        pegasusSampleCount: pegasusTotal,
        sampleStatus:
          pegasusTotal > 0
            ? `${pegasusCompleted}/${pegasusTotal} received`
            : '—',
        photoBridgeDone: photoBridgeStatus.system_status === 'COMPLETED',
        photoBridgeImageCount: photoBridgeTotal,
        imagesDone: `${photoBridgeCompleted}/${photoBridgeTotal}`,
        imagesDoneComplete: photoBridgeStatus.system_status === 'COMPLETED',
        pipelineDone: pipelineStatus.system_status === 'COMPLETED',
        pipelinePartiallyDone: pipelineStatus.system_status === 'IN_PROGRESS',
        pipelineAccepted: pipelineCompleted,
        pipelineRejected: pipelineErrored,
        pipelineImageCount: pipelineTotal,
        pipelineTooltipText: pipelineTotal
          ? `${pipelineCompleted}/${pipelineTotal} accepted · ${pipelineErrored} rejected`
          : 'No images processed',
        allComplete: t.overall_status === 'COMPLETED',
        assetHubDone: assetHubStatus.system_status === 'COMPLETED',
        assetHubPartiallyDone: assetHubStatus.system_status === 'IN_PROGRESS',
        assetHubImageCount: assetHubTotal,
        finalShotCount: photoBridgeTotal || 0,
        gargantuaDone: gargantuaStatus.system_status === 'COMPLETED',
        gargantuaPartiallyDone: gargantuaStatus.system_status === 'IN_PROGRESS',
        gargantuaImageCount: gargantuaStatus.total || 0,
        gargantuaRejected: gargantuaStatus.errored || 0,
        gargantuaAccepted: gargantuaStatus.completed || 0,
        rejectedImages: [],
        previewUrl: tcinPreviewUrlMap.get(t.tcin) || t.preview_url || null,
      }

      return finalData
    })

    return renderMetrics(tcinProgressRows)
  }

  const renderProjectDetailsTable = () => {
    return (
      <CustomTable
        tableName={'projects'}
        uniqueIdName={'tcin'}
        tabName={currentTabValue}
        isLoading={projectLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        columns={columns}
        checkBox
        customizeColumns={
          viewType === 'Userbased'
            ? userCustomizedColumns || []
            : generateDefaultColumns({ currentTabValue })
        }
        userColumns={userCustomizedColumns || []}
        isArchived={
          projectData.archived ||
          userId?.toLowerCase() !==
            projectData.created_by?.login_id?.toLowerCase()
        }
        rows={tcinInfo}
        moreInfoHandler={handleOpenTabData}
        onRowOrderChange={onOrderChange}
        Toolbar={
          <TableToolbar
            isRemoveDisabled={isEditDisabled}
            setTabContent={setTabContent}
            getProjectInfo={getProjectInfoCall}
            isEnableCustomColumns={!isPackagingLikeTab}
            tabPage="Projects"
            subTabPage={currentTabValue}
            packagingAssetUploadedDate={packagingAssetUploadedDate}
            handlePackagingAssetUploadedDateChange={
              handlePackagingAssetUploadedDateChange
            }
          />
        }
        getProjectInfo={getProjectInfoCall}
        projectId={projectId}
        isProducer={userADGroups?.includes(producerADGroup.toUpperCase())}
        viewType={viewType}
        setViewType={setViewType}
        updateUserView={updateUserView}
        packagingAssetUploadedDate={packagingAssetUploadedDate}
      />
    )
  }

  const renderTabsContent = () => {
    switch (currentTabValue) {
      case 'metrics': {
        return renderProjectMetricsTable()
      }
      case 'packaging': {
        return renderProjectDetailsTable()
      }
      case 'additionalImages': {
        return renderProjectDetailsTable()
      }
      default:
        return renderProjectDetailsTable()
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {currentTabValue !== 'metrics' && (
          <Grid container alignItems="center" className={classes.imgInfo}>
            <div className={classes.attrDiv}>
              <Typography variant="subtitle1" className={classes.attrHead}>
                #TCINs
              </Typography>
              <Typography variant="button" className={classes.attrValue}>
                {(projectData.project_stats || {}).tcin_count}
              </Typography>
            </div>
            <Divider orientation="vertical" flexItem />
            <div className={classes.attrDiv}>
              <Typography variant="subtitle1" className={classes.attrHead}>
                Estimated #Images
              </Typography>
              <Typography variant="button" className={classes.attrValue}>
                {(projectData.project_stats || {}).estimated_images}
              </Typography>
            </div>
            <Divider orientation="vertical" flexItem />
            <div className={classes.attrDiv}>
              <Typography variant="subtitle1" className={classes.attrHead}>
                Shoot Complete
              </Typography>
              <Typography variant="button" className={classes.attrValue}>
                {(projectData.project_stats || {}).shoot_complete}
              </Typography>
            </div>
            <Divider orientation="vertical" flexItem />
            <div className={classes.attrDiv}>
              <Typography variant="subtitle1" className={classes.attrHead}>
                In Imaging
              </Typography>
              <Typography variant="button" className={classes.attrValue}>
                {(projectData.project_stats || {}).imaging}
              </Typography>
            </div>
            <Divider orientation="vertical" flexItem />
            <div className={classes.attrDiv}>
              <Typography variant="subtitle1" className={classes.attrHead}>
                Sent to Pipeline
              </Typography>
              <Typography variant="button" className={classes.attrValue}>
                {(projectData.project_stats || {}).pipeline}
              </Typography>
            </div>
          </Grid>
        )}
        {currentTabValue === 'metrics' && renderProjectHealth()}
        <div style={{ marginLeft: 'auto' }}>
          <Button
            variant="text"
            className={classes.tagsButton}
            onClick={() => {
              setOpenDrawer('Project Info')
            }}
          >
            View Project Info
          </Button>
        </div>

        <Drawer
          classes={{
            paper: classes.drawer,
          }}
          anchor="right"
          open={openDrawer.length}
          onClose={() =>
            openDrawer === 'Edit Project'
              ? handleEditClose()
              : setOpenDrawer('')
          }
        >
          <div
            style={{
              height: '100%',
            }}
          >
            <Grid
              wrap={'nowrap'}
              container
              justify={'space-between'}
              alignItems={'center'}
            >
              <h4 className={classes.drawerHeader}>{openDrawer}</h4>
              <IconButton
                aria-label="close"
                onClick={() => setOpenDrawer('')}
                className={classes.closeIcon}
              >
                <CloseIcon style={{ fontSize: 24 }} />
              </IconButton>{' '}
            </Grid>
            {renderDrawer(openDrawer)}
          </div>
        </Drawer>
      </div>
      <CustomTabs
        tabLabels={projectTabLabels}
        renderTabsContent={renderTabsContent}
        setCurrentTabValue={setCurrentTabValue}
        defaultTab={currentTabValue}
        showTabsPanel={false}
      />
      <Drawer
        classes={{
          paper: classes.tabDrawer,
        }}
        anchor="right"
        open={tabData}
        onClose={handleCloseTabData}
      >
        <TabData
          closeDialog={handleCloseTabData}
          tabIndex={tabIndex}
          tcinData={tabContent}
          repurposeLoading={repurposeLoading}
          parentTab={currentTabValue}
          packagingAssetUploadedDate={packagingAssetUploadedDate}
        />
      </Drawer>
    </div>
  )
})

const mapAuthToProps = (auth) => {
  return {
    accessToken: auth.session?.userInfo?.accessToken,
    userId: auth.session?.userInfo?.lanId,
    memberOf: auth.session?.userInfo?.memberOf,
  }
}

export default withAuth(mapAuthToProps)(React.memo(ProjectDetail))
